import * as React from "react";
import { Formik } from "formik";
import { useMsal } from "@azure/msal-react";

import { API_URL_ERROR } from "common/constants/config-error-messages.constants";
import { ReactComponent as MicrosoftIcon } from "common/assets/microsoft-icon.svg";
import UserIdForm from "features/login-screen/login-form/user-id-form/views/user-id-form.component";
import userIdSchema from "features/login-screen/login-form/user-id-form/state/user-id-form.validation";
import PasswordForm from "features/login-screen/login-form/password-form/views/password-form.component";
import passwordSchema from "features/login-screen/login-form/password-form/state/password-form.validation";

import {
  showErrorToast,
  showWarningToast,
  useToastDispatch,
} from "common/context/toast/toast-context";
import {
  BFYI_AUTH_TYPE,
  MSAL_AUTH_TYPE,
} from "common/context/auth/get-authentication-type";
import { useAuth } from "common/context/auth/auth-context";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import makeStyles from "@material-ui/core/styles/makeStyles";
import loginFormStyles from "features/login-screen/login-form/views/login-form.styles";

const useStyles = makeStyles(loginFormStyles);

const initialValues = { userId: "", password: "" };

function LoginForm() {
  const { accounts } = useMsal();

  const classes = useStyles();

  const toastDispatch = useToastDispatch();

  const { submitUserId, MSALLogin, login, MSALTokenLogin } = useAuth();

  const [showPasswordForm, setShowPasswordForm] = React.useState(false);

  const handleSubmitLogin = async (setSubmitting, userId, password) => {
    const submitLoginResult = await login({ userId, password });
    if (submitLoginResult instanceof Error) {
      showErrorToast(
        toastDispatch,
        "An error occurred while trying to sign in",
        submitLoginResult.message,
      );

      setSubmitting(false);
      return;
    }

    setSubmitting(false);
  };

  async function handleSubmitUserId(setSubmitting, userId) {
    const authenticationTypeResult = await submitUserId(userId);
    if (authenticationTypeResult instanceof Error) {
      const { errorTitle, errorMessage } = API_URL_ERROR;

      if (authenticationTypeResult.message === errorTitle) {
        showErrorToast(toastDispatch, errorTitle, errorMessage);
      } else if (
        [
          "Not Found",
          "Failed to fetch",
          "NetworkError when attempting to fetch resource.",
          "A server with the specified hostname could not be found.",
        ].includes(authenticationTypeResult.message)
      ) {
        showErrorToast(
          toastDispatch,
          "Please verify the API URL is set correctly",
          authenticationTypeResult.message,
        );
      } else {
        showErrorToast(
          toastDispatch,
          "An error occurred while submitting the user id",
          authenticationTypeResult.message,
        );
      }

      setSubmitting(false);
      return;
    }

    if (authenticationTypeResult.validation === false) {
      showWarningToast(
        toastDispatch,
        "Validation failed while getting the authentication type",
        authenticationTypeResult.message,
      );

      setSubmitting(false);
      return;
    }

    if (authenticationTypeResult.authenticationType === MSAL_AUTH_TYPE) {
      const MSALLoginResult = await MSALLogin(userId);
      if (MSALLoginResult instanceof Error) {
        showErrorToast(
          toastDispatch,
          "An error occurred with the MSAL authentication",
          MSALLoginResult.message,
        );
      }
    } else if (authenticationTypeResult.authenticationType === BFYI_AUTH_TYPE) {
      setShowPasswordForm(true);
    }

    setSubmitting(false);
  }

  async function handleSubmit(values, { setSubmitting }) {
    const { userId: userIdInput, password: passwordInput } = values;
    const userId = userIdInput.trim();
    const password = passwordInput.trim();

    if (showPasswordForm === false) {
      await handleSubmitUserId(setSubmitting, userId);
    } else {
      await handleSubmitLogin(setSubmitting, userId, password);
    }
  }

  async function handleMSTokenLogin() {
    const msalTokenLoginResult = await MSALTokenLogin();
    if (msalTokenLoginResult instanceof Error) {
      showErrorToast(
        toastDispatch,
        "An error occurred while trying to sign in",
        msalTokenLoginResult.message,
      );
    }
  }

  function handleBackClick() {
    setShowPasswordForm(false);
  }

  return (
    <>
      {accounts.length > 0 ? (
        <div>
          <span className={classes.msSignIn}>
            <Typography variant="body1">
              You have an active Microsoft session
            </Typography>
            <Button
              variant="outlined"
              startIcon={<MicrosoftIcon width="30px" />}
              onClick={handleMSTokenLogin}
              fullWidth
              className={classes.msSignInButton}
            >
              Sign in
            </Button>
          </span>
          <Divider />
        </div>
      ) : null}
      <Formik
        validationSchema={
          showPasswordForm === false ? userIdSchema : passwordSchema
        }
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {showPasswordForm === false ? (
          <UserIdForm />
        ) : (
          <PasswordForm onBackClick={handleBackClick} />
        )}
      </Formik>
    </>
  );
}

export default LoginForm;
